import { useState } from 'react'
import { Card, Button, Typography } from '@arco-design/web-react'
import { IconGithub, IconSend, IconSafe } from '@arco-design/web-react/icon'
import { useTranslation } from 'react-i18next'
import { Pill } from '@/utils/render'
import { useOAuthBindings } from '@/hooks/useOAuthBindings'
import WeChatBindModal from './modals/WeChatBindModal'

interface OAuthBindingsCardProps {
  runSecure: <T>(fn: () => Promise<T>) => Promise<T>
  onRefresh: () => Promise<void>
}

const BUILTIN_PROVIDERS = [
  { key: 'github', label: 'GitHub', enabledKey: 'github_oauth' as const, idKey: 'githubId' as const, icon: <IconGithub /> },
  { key: 'linuxdo', label: 'LinuxDo', enabledKey: 'linuxdo_oauth' as const, idKey: 'linuxdoId' as const, icon: <span className="font-bold">L</span> },
  { key: 'discord', label: 'Discord', enabledKey: 'discord_oauth' as const, idKey: 'discordId' as const, icon: <span className="font-bold">D</span> },
  { key: 'oidc', label: 'OIDC', enabledKey: 'oidc_enabled' as const, idKey: 'oidcId' as const, icon: <IconSafe /> },
  { key: 'wechat', label: 'WeChat', enabledKey: 'wechat_login' as const, idKey: 'wechatId' as const, icon: <span className="font-bold">W</span> },
  { key: 'telegram', label: 'Telegram', enabledKey: 'telegram_oauth' as const, idKey: 'telegramId' as const, icon: <IconSend /> },
]

export default function OAuthBindingsCard({ runSecure, onRefresh }: OAuthBindingsCardProps) {
  const { t } = useTranslation()
  const [wechatOpen, setWechatOpen] = useState(false)
  const {
    user,
    status,
    isBound,
    bindProvider,
    bindCustom,
    unbindCustom,
    getCustomBinding,
    unbindLoading,
  } = useOAuthBindings(runSecure)

  const visibleBuiltin = BUILTIN_PROVIDERS.filter((p) => {
    const enabled = status?.[p.enabledKey]
    return enabled === true
  })

  const customProviders = status?.custom_oauth_providers || []

  if (visibleBuiltin.length === 0 && customProviders.length === 0) return null

  return (
    <>
      <Card className="tt-card !rounded-2xl !border-[var(--console-border)]" title={t('personal.oauth.title')}>
        <div className="space-y-3">
          {visibleBuiltin.map((p) => {
            const boundId = user?.[p.idKey]
            const bound = isBound(boundId)
            return (
              <div
                key={p.key}
                className="flex items-center justify-between gap-3 p-3 rounded-xl border border-[var(--console-border)]"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-[var(--console-page-bg)] flex items-center justify-center flex-shrink-0">
                    {p.icon}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm">{p.label}</div>
                    <Typography.Text type="secondary" className="text-xs truncate block">
                      {bound ? boundId : t('personal.oauth.unbound')}
                    </Typography.Text>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Pill variant={bound ? 'success' : 'neutral'}>{bound ? t('personal.oauth.bound') : t('personal.oauth.unbound')}</Pill>
                  {p.key === 'wechat' ? (
                    <Button size="small" type="outline" onClick={() => setWechatOpen(true)}>
                      {bound ? t('personal.oauth.bound') : t('personal.oauth.bind')}
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      type="outline"
                      disabled={bound || p.key === 'telegram'}
                      onClick={() => void bindProvider(p.key)}
                    >
                      {t('personal.oauth.bind')}
                    </Button>
                  )}
                </div>
              </div>
            )
          })}

          {customProviders.map((provider) => {
            const binding = getCustomBinding(provider.id)
            const bound = Boolean(binding)
            return (
              <div
                key={provider.slug}
                className="flex items-center justify-between gap-3 p-3 rounded-xl border border-[var(--console-border)]"
              >
                <div className="min-w-0">
                  <div className="font-medium text-sm">{provider.name}</div>
                  <Typography.Text type="secondary" className="text-xs truncate block">
                    {bound ? binding?.provider_user_id : t('personal.oauth.unbound')}
                  </Typography.Text>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {bound ? (
                    <Button
                      size="small"
                      status="danger"
                      type="outline"
                      loading={unbindLoading[provider.id]}
                      onClick={() => void unbindCustom(provider.id)}
                    >
                      {t('personal.oauth.unbind')}
                    </Button>
                  ) : (
                    <Button size="small" type="outline" onClick={() => void bindCustom(provider)}>
                      {t('personal.oauth.bind')}
                    </Button>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </Card>

      <WeChatBindModal visible={wechatOpen} onClose={() => setWechatOpen(false)} onSuccess={onRefresh} />
    </>
  )
}
